<script setup lang="ts">
const props = defineProps({
  craftable: {
    type: Object as PropType<CraftItem>,
    required: true,
  },
});

const { getMerchantAttributes } = useLuxuryDesign();
const craftableDesign = computed(() =>
  getMerchantAttributes(props.craftable.merchantId)
);

onUnmounted(() => {
  useAppStateStore().currentMerchant = props.craftable.merchantId as MerchantId;
});

const canCraft = computed(() => {
  return props.craftable.ingredients.every(
    (ingredient) =>
      (ingredient.availableQuantity ?? 0) >= ingredient.requiredQuantity
  );
});

const isCraftableComingSoon = computed(() => {
  if (props.craftable.start) {
    const startDate = new Date(props.craftable.start);
    const now = new Date();
    return startDate > now;
  }
  return false;
});

const creationNumber = computed(() =>
  isCraftableComingSoon.value ? "?" : props.craftable.number ?? undefined
);
const isCraftableStartShowable = computed(() => {
  if (props.craftable.start) {
    const startDate = new Date(props.craftable.start);
    const maxShowableYear = new Date(2099, 1);
    return startDate < maxShowableYear;
  }
  return false;
});
</script>

<template>
  <div
    v-bind="craftableDesign"
    class="border-2 border-primary-900 rounded-lg merchant-card">
    <div class="xl:grid xl:grid-cols-2">
      <CardDetailImage
        :merchantId="craftable.merchantId"
        :rarity="craftable.rarity ? craftable.rarity : ''"
        :image-id="craftable.imageId"
        :units-left="
          craftable.number && craftable.number <= 10
            ? craftable.number
            : undefined
        "
        :number="craftable.number ?? undefined"
        :copies="craftable.totalCopies ?? undefined"
        :level="craftable.level ? craftable.level : undefined"
        :is-craftable="true"
        :properties="craftable.properties" />
      <div
        class="relative lg:col-start-2 lg:row-span-3 flex flex-col border-t border-l-0 xl:border-l xl:border-t-0 border-primary-900">
        <CardDetailShare />
        <CardDetailTitle
          :id="craftable.id"
          :translation-id="craftable.translationId"
          :merchantId="craftable.merchantId"
          :categoryId="craftable.categoryId"
          :one-time="craftable.oneTime"
          :offer-text="
            craftable.oneTime && $te(`${craftable.translationId}.offer`)
              ? $t(`${craftable.translationId}.offer`)
              : ''
          "
          :number="craftable.number ?? 0"
          :total="craftable.totalCopies ?? 0" />
        <CardDetailSupply
          v-if="craftable.totalCopies && !isCraftableComingSoon"
          :supply="craftable.totalCopies"
          :number="craftable.number ?? 0" />
        <div
          v-if="craftable.number !== undefined || craftable.level !== undefined"
          class="px-7 py-4 border-b-2 border-primary-900">
          <CardDetailMeta
            :number="craftable.number ?? undefined"
            :display-number="creationNumber"
            :level="craftable.level ?? undefined" />
        </div>
        <CardDetailDescription :translationId="craftable.translationId" />
        <CardDetailProperties
          :rarity="craftable.rarity ? craftable.rarity : ''"
          :image-id="craftable.imageId"
          :properties="craftable.properties"
          :merchant-id="craftable.merchantId" />
        <CardDetailCraft
          :craftable-id="craftable.id"
          :price="craftable.price?.GFAL"
          :text="$t('main.craftableDetail.requires')"
          :crafting-requirements="craftable.ingredients"
          :show-requirements="craftable.showRequirements" />
        <CardDetailCraftButton
          v-if="!isCraftableComingSoon"
          :item="craftable"
          :disabled="!canCraft" />
        <div
          v-if="craftable.start && isCraftableComingSoon && false"
          class="border-b-2 border-primary-900 border-t px-2 py-5 flex justify-center items-center merchant-card__surface">
          <p
            class="text-secondary-200 font-semibold text-md text-center w-full">
            {{ $t("main.home.comingSoon") }}
          </p>
        </div>
        <div>
          <Transition mode="out-in">
            <div
              v-if="
                isCraftableStartShowable &&
                isCraftableComingSoon &&
                craftable.start
              "
              class="text-md flex items-center gap-1 px-2 py-6 overflow-hidden rounded-b-lg pl-6">
              <NuxtIcon
                name="mdi:clock-outline"
                class="text-neutral-200 w-6 h-6" />
              <p class="text-secondary-200">{{ $t(`main.availableOn`) }}</p>
              <p class="text-neutral-200 font-semibold">
                {{
                  new Date(craftable.start).toLocaleDateString($i18n.locale, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }}
              </p>
            </div></Transition
          >
        </div>
      </div>
    </div>
  </div>
</template>
